/**
 * Role-Based Access Control — Frontend Role & Permission Definitions
 *
 * Roles are resolved from the backend (database-backed).
 * The resolveRoles() function is kept as a fallback for when the backend
 * hasn't responded yet (e.g. on first page load before /users/me returns).
 *
 * All authenticated OKTA users receive Contributor access by default.
 * Elevated operating roles must be assigned by an admin or program manager.
 *
 * VITE_ADMIN_EMAILS    — comma-separated fallback admin emails
 */

// ─── Role Constants (mirrors backend/src/constants/roles.js) ──────
export const ROLES = {
  CONTRIBUTOR: 'contributor',
  TEAM_LEAD: 'team_lead',
  PROGRAM_MANAGER: 'program_manager',
  ADMIN: 'admin',
}

// ─── Permission Constants ─────────────────────────────────────────
export const PERMISSIONS = {
  INITIATIVE_VIEW: 'initiative:view',
  INITIATIVE_CREATE: 'initiative:create',
  INITIATIVE_EDIT_OWN: 'initiative:edit:own',
  INITIATIVE_DELETE_OWN: 'initiative:delete:own',
  INITIATIVE_EDIT_TEAM: 'initiative:edit:team',
  INITIATIVE_DELETE_TEAM: 'initiative:delete:team',
  INITIATIVE_EDIT_ANY: 'initiative:edit:any',
  INITIATIVE_DELETE_ANY: 'initiative:delete:any',
  INITIATIVE_SUBMIT_AWARDS: 'initiative:submit_awards',
  SCORE_VIEW: 'score:view',
  SCORE_SUBMIT: 'score:submit',
  PANELIST_MANAGE: 'panelist:manage',
  DASHBOARD_VIEW: 'dashboard:view',
  REPORTS_VIEW_ALL: 'reports:view:all',
  AWARDS_MANAGE: 'awards:manage',
  TAXONOMY_MANAGE: 'taxonomy:manage',
  USERS_VIEW: 'users:view',
  USERS_MANAGE: 'users:manage',
  ROLES_ASSIGN: 'roles:assign',
  SYSTEM_CONFIG: 'system:config',
  STRETCH_VIEW: 'stretch:view',
  STRETCH_CREATE: 'stretch:create',
  STRETCH_PARTICIPATE: 'stretch:participate',
}

/**
 * Local fallback: derives roles from email when back-end hasn't responded.
 * Once the auth store fetches /users/me, these are replaced with DB roles.
 */
export function resolveRoles(email) {
  if (!email) return [ROLES.CONTRIBUTOR]

  const normalised = email.trim().toLowerCase()

  const adminEmails = (import.meta.env.VITE_ADMIN_EMAILS || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)

  const roles = [ROLES.CONTRIBUTOR]
  if (adminEmails.includes(normalised)) roles.push(ROLES.ADMIN)

  return roles
}

/**
 * Check if a set of roles includes a specific permission.
 * Mirrors the backend hasPermission logic.
 */
export function hasPermission(userRoles, permission) {
  if (!userRoles || !permission) return false
  const roleList = Array.isArray(userRoles) ? userRoles : [userRoles]
  // Admin has all permissions
  if (roleList.includes(ROLES.ADMIN)) return true
  // Check role-permission mapping (simplified client-side check)
  return roleList.some((role) => {
    const perms = ROLE_PERMISSIONS[role]
    return perms && perms.includes(permission)
  })
}

// ─── Role → Permission Mapping (client-side mirror) ───────────────
const ROLE_PERMISSIONS = {
  [ROLES.CONTRIBUTOR]: [
    PERMISSIONS.INITIATIVE_VIEW,
    PERMISSIONS.INITIATIVE_CREATE,
    PERMISSIONS.INITIATIVE_EDIT_OWN,
    PERMISSIONS.INITIATIVE_DELETE_OWN,
    PERMISSIONS.INITIATIVE_SUBMIT_AWARDS,
    PERMISSIONS.SCORE_VIEW,
    PERMISSIONS.DASHBOARD_VIEW,
  ],
  [ROLES.TEAM_LEAD]: [
    PERMISSIONS.INITIATIVE_VIEW,
    PERMISSIONS.INITIATIVE_CREATE,
    PERMISSIONS.INITIATIVE_EDIT_OWN,
    PERMISSIONS.INITIATIVE_DELETE_OWN,
    PERMISSIONS.INITIATIVE_EDIT_TEAM,
    PERMISSIONS.INITIATIVE_DELETE_TEAM,
    PERMISSIONS.INITIATIVE_SUBMIT_AWARDS,
    PERMISSIONS.SCORE_VIEW,
    PERMISSIONS.DASHBOARD_VIEW,
  ],
  [ROLES.PROGRAM_MANAGER]: [
    PERMISSIONS.INITIATIVE_VIEW,
    PERMISSIONS.INITIATIVE_CREATE,
    PERMISSIONS.INITIATIVE_SUBMIT_AWARDS,
    PERMISSIONS.SCORE_VIEW,
    PERMISSIONS.SCORE_SUBMIT,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.REPORTS_VIEW_ALL,
    PERMISSIONS.PANELIST_MANAGE,
    PERMISSIONS.AWARDS_MANAGE,
    PERMISSIONS.TAXONOMY_MANAGE,
    PERMISSIONS.USERS_VIEW,
  ],
  [ROLES.ADMIN]: Object.values(PERMISSIONS),
}
